import React, {
  ReactElement,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";

const TypingEffect = ({
  htmlContent,
  speed = 50,
}: {
  htmlContent: string | Promise<string>;
  speed: number;
}): ReactElement => {
  // State to store the progressively displayed content
  const [displayed, setDisplayed] = useState("");
  // Ref to ensure scrolling to the bottom
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent as string, "text/html");
    // Parse the HTML content into elements
    const elements = Array.from(doc.body.childNodes);

    // Tracks the current element being processed
    let index = 0;
    // Accumulates the displayed content
    let currentContent = "";

    // Function to type out the next element
    const typeNext = (): void => {
      if (index < elements.length) {
        const element = elements[index];

        if (element.nodeType === Node.TEXT_NODE) {
          // Handle plain text nodes
          currentContent += element.textContent;
          setDisplayed(currentContent);
          index++;
          setTimeout(typeNext, speed);
        } else if (element.nodeType === Node.ELEMENT_NODE) {
          // Handle HTML elements
          if (["P", "LI"].includes(element.nodeName)) {
            // Helper to type out HTML elements, handling nested <strong> tags
            const typeHtmlElement = (el: ChildNode, done: () => void): void => {
              if (el.nodeType === Node.TEXT_NODE) {
                const chars = el.textContent?.split("");
                let charIndex = 0;
                const typeText = (): void => {
                  if (chars && charIndex < chars.length) {
                    currentContent += chars[charIndex];
                    setDisplayed(currentContent);
                    charIndex++;
                    setTimeout(typeText, speed);
                  } else {
                    done();
                  }
                };
                typeText();
              } else if (el.nodeType === Node.ELEMENT_NODE) {
                const tag = (el as Element).nodeName.toLowerCase();
                currentContent += `<${tag}${getAttributes(el as Element)}>`;
                setDisplayed(currentContent);

                const children = Array.from(el.childNodes);
                let childIdx = 0;
                const typeChildren = (): void => {
                  if (childIdx < children.length) {
                    typeHtmlElement(children[childIdx], () => {
                      childIdx++;
                      typeChildren();
                    });
                  } else {
                    currentContent += `</${tag}>`;
                    setDisplayed(currentContent);
                    done();
                  }
                };
                typeChildren();
              } else {
                done();
              }
            };

            const tag = element.nodeName.toLowerCase();
            currentContent += `<${tag}${getAttributes(element as Element)}>`;
            setDisplayed(currentContent);

            const children = Array.from(element.childNodes);
            let childIdx = 0;
            const typeChildren = (): void => {
              if (childIdx < children.length) {
                typeHtmlElement(children[childIdx], () => {
                  childIdx++;
                  typeChildren();
                });
              } else {
                currentContent += `</${tag}>`;
                setDisplayed(currentContent);
                index++;
                typeNext();
              }
            };
            typeChildren();
          } else if (["UL", "OL"].includes(element.nodeName)) {
            // Handle lists and their children
            currentContent += `<${element.nodeName.toLowerCase()}${getAttributes(
              element as Element
            )}>`;
            setDisplayed(currentContent);

            const listItems = Array.from(element.childNodes).filter(
              (child) => child.nodeName === "LI"
            );

            let liIndex = 0;

            const typeListItem = (): void => {
              if (liIndex < listItems.length) {
                const li = listItems[liIndex];
                // Use the same recursive typing for list items
                const tag = li.nodeName.toLowerCase();
                currentContent += `<${tag}${getAttributes(li as Element)}>`;
                setDisplayed(currentContent);

                const children = Array.from(li.childNodes);
                let childIdx = 0;
                const typeChildren = (): void => {
                  if (childIdx < children.length) {
                    // Use the same helper for nested strong, em, etc.
                    typeHtmlElement(children[childIdx], () => {
                      childIdx++;
                      typeChildren();
                    });
                  } else {
                    currentContent += `</${tag}>`;
                    setDisplayed(currentContent);
                    liIndex++;
                    typeListItem();
                  }
                };
                typeChildren();
              } else {
                currentContent += `</${element.nodeName.toLowerCase()}>`;
                setDisplayed(currentContent);
                index++;
                typeNext();
              }
            };

            // Define typeHtmlElement for list context
            const typeHtmlElement = (el: ChildNode, done: () => void): void => {
              if (el.nodeType === Node.TEXT_NODE) {
                const chars = el.textContent?.split("");
                let charIndex = 0;
                const typeText = (): void => {
                  if (chars && charIndex < chars.length) {
                    currentContent += chars[charIndex];
                    setDisplayed(currentContent);
                    charIndex++;
                    setTimeout(typeText, speed);
                  } else {
                    done();
                  }
                };
                typeText();
              } else if (el.nodeType === Node.ELEMENT_NODE) {
                const tag = (el as Element).nodeName.toLowerCase();
                currentContent += `<${tag}${getAttributes(el as Element)}>`;
                setDisplayed(currentContent);

                const children = Array.from(el.childNodes);
                let childIdx = 0;
                const typeChildren = (): void => {
                  if (childIdx < children.length) {
                    typeHtmlElement(children[childIdx], () => {
                      childIdx++;
                      typeChildren();
                    });
                  } else {
                    currentContent += `</${tag}>`;
                    setDisplayed(currentContent);
                    done();
                  }
                };
                typeChildren();
              } else {
                done();
              }
            };

            typeListItem();
          } else {
            // Directly append unsupported elements
            currentContent += (element as Element).outerHTML;
            setDisplayed(currentContent);
            index++;
            setTimeout(typeNext, speed);
          }
        }
      }
    };

    // Helper function to extract attributes from an element
    const getAttributes = (element: Element): ReactNode => {
      return Array.from(element.attributes)
        .map((attr) => ` ${attr.name}="${attr.value}"`)
        .join("");
    };

    typeNext();

    return (): void => {
      // Stop typing on unmount
      index = elements.length;
    };
  }, [htmlContent, speed]);

  useEffect(() => {
    // Scroll to the bottom whenever displayed content updates
    if (containerRef.current) {
      containerRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [displayed]);

  return (
    <div>
      <span dangerouslySetInnerHTML={{ __html: displayed }} />
      <div ref={containerRef} />
    </div>
  );
};

export default TypingEffect;
